import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

/**
 * ChevronProgressTracker - Domino's-style progress tracker for septic system projects
 * 
 * @param {string} currentStatus - Current status of the project
 * @param {object} statusDates - Optional dates for each status step
 * @param {string} description - Optional description text below tracker
 * @param {boolean} showAnimation - Show celebratory animation when complete
 */
const ChevronProgressTracker = ({ 
  currentStatus, 
  statusDates = {}, 
  description = null,
  showAnimation = true 
}) => {
  const [showConfetti, setShowConfetti] = useState(false);

  const steps = [
    { 
      id: 1, 
      label: 'Pending Review', 
      icon: 'Eye',
      description: 'Pending review',
      statusMatch: ['New', 'Pending Review']
    },
    { 
      id: 2, 
      label: 'Establishing Contact', 
      icon: 'Phone',
      description: 'Establishing contact',
      statusMatch: ['Contacted']
    },
    { 
      id: 3, 
      label: 'Pending Survey / Design', 
      icon: 'Clipboard',
      description: 'Site survey and system design',
      statusMatch: ['Pending Design', 'Pending Survey', 'Working']
    },
    { 
      id: 4, 
      label: 'Pending Permitting', 
      icon: 'FileText',
      description: 'Awaiting permit approval',
      statusMatch: ['Pending Permiting', 'Pending Permitting']
    },
    { 
      id: 5, 
      label: 'Install Scheduled', 
      icon: 'Calendar',
      description: 'Installation date confirmed',
      statusMatch: ['Pending Install', 'Install Scheduled']
    },
    { 
      id: 6, 
      label: 'Pending Inspection', 
      icon: 'Search',
      description: 'Final inspection in progress',
      statusMatch: ['Pending Inspection']
    },
    { 
      id: 7, 
      label: 'Complete', 
      icon: 'CheckCircle',
      description: 'Project successfully completed',
      statusMatch: ['Complete', 'Converted', 'Closed']
    }
  ];

  // Determine current step index
  const getCurrentStepIndex = () => {
    const index = steps?.findIndex(step => 
      step?.statusMatch?.some(status => 
        status?.toLowerCase() === currentStatus?.toLowerCase()
      )
    );
    return index >= 0 ? index : 0;
  };

  const currentStepIndex = getCurrentStepIndex();

  // Show confetti animation when reaching Complete
  useEffect(() => {
    if (currentStepIndex === 6 && showAnimation) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [currentStepIndex, showAnimation]);

  const getStepState = (index) => {
    if (index < currentStepIndex) return 'completed';
    if (index === currentStepIndex) return 'current';
    return 'future';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDefaultDescription = () => {
    if (currentStepIndex === 6) {
      return "🎉 Congratulations! Your septic system project is complete!";
    }
    const nextStep = steps?.[currentStepIndex + 1];
    if (nextStep) {
      return `${steps?.[currentStepIndex]?.description} — next step: ${nextStep?.label}`;
    }
    return steps?.[currentStepIndex]?.description;
  };

  return (
    <div className="w-full">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {[...Array(50)]?.map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-20px',
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: ['#1e40af', '#0891b2', '#059669', '#f97316']?.[Math.floor(Math.random() * 4)],
                }}
              />
            </div>
          ))}
        </div>
      )}
      
      {/* Mobile Horizontal Scroll Container - Hidden on mobile, shown on tablet+ */}
      <div className="hidden md:block overflow-x-hidden pb-4">
        <div className="flex items-center justify-start w-full gap-0">
          {steps?.map((step, index) => {
            const state = getStepState(index);
            const stepDate = statusDates?.[step?.statusMatch?.[0]];
            const isLast = index === steps?.length - 1;

            return (
              <div key={step?.id} className="relative flex items-center">
                {/* Chevron Step */}
                <div 
                  className="relative"
                >
                  {/* Main Chevron Shape */}
                  <div
                    className={`
                      relative flex items-center justify-center
                      transition-all duration-300 ease-in-out
                      ${state === 'completed' ? 'bg-primary text-primary-foreground elevation-2' : ''}
                      ${state === 'current' ? 'text-white elevation-3 animate-pulse-subtle' : ''}
                      ${state === 'future' ? 'bg-muted text-muted-foreground' : ''}
                      ${index === 0 ? 'rounded-l-lg' : ''}
                      ${isLast ? 'rounded-r-lg' : ''}
                    `}
                    style={{
                      clipPath: isLast 
                        ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
                        : 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)',
                      minWidth: index === 0 ? '200px' : '220px',
                      height: '80px',
                      marginLeft: index === 0 ? '0' : '-20px',
                      paddingLeft: index === 0 ? '20px' : '30px',
                      paddingRight: isLast ? '20px' : '30px',
                      ...(state === 'current' && { backgroundColor: '#2b75c9' }),
                    }}
                  >
                    {/* Current Step Glow Effect */}
                    {state === 'current' && (
                      <div className="absolute inset-0 opacity-20 blur-xl rounded-lg animate-pulse" style={{ backgroundColor: '#2b75c9' }} />
                    )}

                    {/* Step Content */}
                    <div className="relative z-10 flex flex-col items-center justify-center gap-2 px-2">
                      {/* Icon */}
                      <div className={`
                        rounded-full p-2 transition-all duration-300
                        ${state === 'completed' ? 'bg-primary-foreground/20' : ''}
                        ${state === 'current' ? 'bg-white/20' : ''}
                        ${state === 'future' ? 'bg-muted-foreground/10' : ''}
                      `}>
                        <Icon 
                          name={state === 'completed' ? 'Check' : step?.icon} 
                          size={20}
                          className={`
                            ${state === 'completed' ? 'text-primary-foreground' : ''}
                            ${state === 'current' ? 'text-white' : ''}
                            ${state === 'future' ? 'text-muted-foreground' : ''}
                          `}
                        />
                      </div>
                      
                      {/* Label */}
                      <p className={`
                        text-xs font-medium text-center leading-tight
                        ${state === 'completed' ? 'text-primary-foreground' : ''}
                        ${state === 'current' ? 'text-white' : ''}
                        ${state === 'future' ? 'text-muted-foreground' : ''}
                      `}>
                        {step?.label}
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Description Text - Always visible, acts as mobile-only status on small screens */}
      {(description || getDefaultDescription()) && (
        <div className="mt-4 md:mt-4">
          <p className="text-sm text-muted-foreground flex items-center justify-start gap-2">
            <Icon name="Info" size={16} className="flex-shrink-0" />
            <span className="text-left">{description || getDefaultDescription()}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default ChevronProgressTracker;